/**
 * All these methods here are duplicated within the methods present
 * on the cart-util.ts. It's being duplicated due the cyclic dependency...
 * ReferenceError: Cannot access 'userProfileStateInitialState' before initialization
 * It's happening because the `user-profile.thunks.ts` imports the `cart-util.ts`,
 * `cart-util.ts` imports `store.ts` itself along with other modules, instantiating
 * the store before the `userProfileStateInitialState` instantiation.
 * 
 * I did try to remove the store from the `cart-util.ts`, but that didn't fixed the
 * issue. That means another modules imported by `cart-util.ts` are also importing
 * the `store.ts`.
 * 
 * This command can be executed to get all the circular dependencies we have.
 *  npx madge --circular --extensions ts ./
 */

import environment, { ENV } from '../../../common/environment';
import { isLMSFlow } from '../../../common/utils/my-account-utils';
import { isHeStorePurchaseFlow, isK12StorePurchaseFlow } from '../../../common/utils/util';
import { AccountPageAppFlow } from '../../my-account/app-flow.enum';
import { UserProfile } from '../user-profile.interface';

const INSTRUCTOR_ROLE = 'instructor';

export function isHEInstructor(user: UserProfile) {
  return isInstructor(user) && user.persona?.segment.toLowerCase() === 'he';
}

export function isInstructor(user: UserProfile) {
  return user?.userType == INSTRUCTOR_ROLE;
}

export function getCartPageUrl(user: UserProfile | undefined, store = 'national') {
  const env = environment();
  if (user && isHEInstructor(user)) {
    if (env.env === ENV.local) {
      return `${window.location.origin}/content/mheducation/us/en/checkout/sample.html?wcmmode=disabled`;
    } else {
      return `${env.mheducationUrl}/checkout/sample.html`;
    }
  } else if (env.env === ENV.local) {
    if (isCustomStore(store)) {
      return `${window.location.origin}/content/mheducation/us/en/checkout/cartk12storefront.store-${store}.html?wcmmode=disabled`;
    } else {
      return `${window.location.origin}/content/mheducation/us/en/checkout/cart.html?wcmmode=disabled`;
    }
  } else {
    return `${env.mheducationUrl}/checkout${getStorePath(store)}/cart`;
  }
}

export function isCustomStore(storeCode: string | undefined): boolean {
  return !!storeCode && storeCode != 'national';
}

export function getStorePath(store: string) {
  if (isK12StorePurchaseFlow()) {
    return '/' + store;
  } else if (isHeStorePurchaseFlow()) {
    return '/highered-store';
  } else {
    return '';
  }
}

export function getAppFlow() {
  const url = new URL(location.href);
  const host = url.searchParams.get('app') || url.searchParams.get('redirectUrl') || location.host;
  const isAPR = !!url.searchParams.get('productId');

  if(host?.includes("connect") && isLMSFlow()) {
    return AccountPageAppFlow.LTIA;
  } else if(host?.includes("newconnect")) {
    return AccountPageAppFlow.Connect;
  } else if(host?.includes("connect")) {
    return AccountPageAppFlow.ClassicConnect;
  } else if(host?.includes("myebooks")) {
    return AccountPageAppFlow.Ebookshelf;
  } else if(host?.includes("firstaid") || host?.includes("dfa")) {
    return AccountPageAppFlow.Dfa;
  } else if(host?.includes("create")) {
    return AccountPageAppFlow.Create;
  } else if(host?.match(/cr.*\.mheducation\.com/)) {
    return AccountPageAppFlow.ClinicalReasoning;
  } else if(host?.includes("nutritioncalc")) {
    return AccountPageAppFlow.NutriCalc;
  } else if(host?.includes("caas")) {
    return AccountPageAppFlow.caas;
  } else if(isAPR) {
    return AccountPageAppFlow.APR;
  }

  return AccountPageAppFlow.Shop;
}

export function getUserActiveTab(): string {
  const tabsProps = [
    {
      title: 'My Information',
      id: '_myInfo',
    },
    {
      title: 'Security & Login',
      id: '_securityLogin',
    },
    {
      title: 'Order History',
      id: '_orderHistory',
    },
    {
      title: 'My Digital Products',
      id: '_myDigitalProducts',
    },
  ]
  const activeTabPane = document.querySelector('.tab-pane.active');
  if (activeTabPane) {
    const activeTabId = activeTabPane.id;
    const activeTab = tabsProps.find(tab => tab.id === activeTabId);
    if (activeTab) {
      return activeTab.title;
    }
  }
  return '';
}
